import Urls from '../urls.js';
import { fetch_status, fetch_json, fetch_error } from '../../../../scripts/utils/fetch.js';
import BaseView from '../../../../scripts/utils/base-view.js';
import AddToCartModalView from './cart-modal.js';
import AddToCartMsgView from './message.js';

export default class AddToCartView extends BaseView {
	constructor({
		el,
		product_id,
		cart_product,
		in_stock_qty,
		allow_duplicates,
		show_popup,
		adv_source,
		map_policy,
		variations,
		track_ga_events,
		is_accessory,
		cart_modal_placeholder,
		options_active
	}) {
		super(el);

		this.selectors = {
			qty: 'input[name="quantity"]',
			btnAddToCart: 'button[name="addtocart"]',
			message: '.add-to-cart-message',
			rewardPoints: '.reward-points'
		};

		this.cart_modal_view = null;
		this.valid_product_option = false;
		this.product_data_options = null;
		this.options_manual = null;
		this.template_url = Urls.Templates.app;

		this.product_id = product_id;
		this.product_price = null;
		this.cart_product = cart_product;
		this.in_stock_qty =  in_stock_qty;
		this.allow_duplicates = allow_duplicates;
		this.show_popup = show_popup;
		this.adv_source = adv_source;
		this.map_policy = map_policy;
		this.variations = variations;
		this.track_ga_events = track_ga_events;
		this.is_accessory = is_accessory;
		this.cart_modal_placeholder = cart_modal_placeholder;
		this.options_active = options_active;
		this.in_cart = cart_product && !allow_duplicates;
		this.hasAddons = false;

		if (this.in_stock_qty < 0 || window.SiteSettings.STOCK_SELL_OUT_OF_STOCK) {
			this.in_stock_qty = window.SiteSettings.SHOPPING_CART_MAX_QTY ? window.SiteSettings.SHOPPING_CART_MAX_QTY : 1000;
		}

		if( !this.is_accessory ) {
			this.render();
			this.bindCartEvents();
		}

		if (this.options_active) {
			this.handleEvents();
		}

		this.getProductPrice();

		if (window.ProductSettings && window.ProductSettings.AddWarranty) {
			this.hasAddons = true;
		}
	}

	get_context () {
		return {
			in_cart: this.in_cart,
			in_stock_qty: this.in_stock_qty,
			label: this.el ? this.el.getAttribute('data-label') : '',
			qty_step: window.ProductSettings.QtyStep ? window.ProductSettings.QtyStep : 1,
			show_qty: window.ProductSettings.ShowCartQty ? window.ProductSettings.ShowCartQty : null,
			min_qty: this.calculateMinQty(),
			product_price: this.product_price
		};
	}

	after_render () {
		const rendered_event = new CustomEvent('[add-to-cart]:rendered');

		this.removeListeners();
		this.bindSelectors();
		this.addListeners();
		this.calculateRewards();

		document.dispatchEvent(rendered_event);
	}

	bindCartEvents () {
		document.addEventListener('[add-to-cart]:add-product', () => {
			this.btnAddToCartElement.click();
		} );
	}

	addListeners () {
		if (this.btnAddToCartElement) {
			this.btnAddToCartElement.addEventListener('click', this.addToCart.bind(this));
		}
		if (this.qtyElement) {
			this.qtyElement.addEventListener('input', this.qtyInput.bind(this));
		}
	}

	removeListeners () {
		if (this.btnAddToCartElement) {
			this.btnAddToCartElement.removeEventListener('click', this.addToCart);
		}
		if (this.qtyElement) {
			this.qtyElement.removeEventListener('input', this.qtyInput);
		}
	}

	qtyInput () {
		this.validateQty();
		this.calculateRewards();
	}

	addToCart (e) {
		e.preventDefault();
		let qty_error = this.validateQtyStep();
		if (!qty_error) {
			if (this.options_active) {
				if (window.ProductSettings.requiredOptions && window.ProductSettings.requiredOptions.length && !this.in_cart) {
					document.dispatchEvent(new CustomEvent('product-options:collect-required'));
					document.addEventListener('product-options:required-set', this.getOptionsAndProceed.bind(this));
				} else {
					this.getOptionsAndProceed();
				}
			} else {
				this.addToCartClicked();
			}
		}
	}

	getOptionsAndProceed () {
		document.dispatchEvent(new CustomEvent('product-options:collect-data'));
		if (this.valid_product_option) {
			this.addToCartClicked();
		}
	}

	addToCartClicked () {
		if (this.in_cart) {
			this.goToCart();
		} else {
			this.addProduct();
		}
	}

	addProduct () {
		const product_data = {
			ProductId: this.product_id,
			ParentId: null,
			Qty: parseInt(this.qtyElement.value),
			DefaultOptions: this.btnAddToCartElement.getAttribute('data-default-options')
		};

		const upd_data = this.getExternalData();
		const data = {...upd_data, ...product_data};

		this.startProgress();

		fetch(Urls.Api.product, {
			method: 'POST',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
		})
			.then(fetch_status)
			.then(fetch_json)
			.then(function (json) {
				this.cart_product = json.Cart.Products.find(product => product.Id === json.CartProductId);
				if (this.options_active && this.options_manual.length > 0) {
					this.addManualOptions(this.options_manual);
				} else {
					this.productAdded(json);
				}
			}.bind(this))
			.catch(error => {
				fetch_error(error, true)
					.then(error_json => {
						this.processError(error_json);
					});
			});
	}

	startProgress () {
		this.btnAddToCartElement.setAttribute('disabled', true);
		this.btnAddToCartElement.classList.add('progress');
		this.qtyElement.setAttribute('disabled', true);
	}

	endProgress () {
		this.btnAddToCartElement.removeAttribute('disabled');
		this.btnAddToCartElement.classList.remove('progress');
		this.qtyElement.removeAttribute('disabled');
	}

	processError (error) {
		let message = 'Something went wrong...';

		if (error.Type && error.Type === 'SessionVerificationRequiredException') {
			if (window.verifySession) {
				window.verifySession();
			}
		} else {
			if (error && typeof error === 'object') {
				message = error.responseJSON && error.responseJSON.Message ? error.responseJSON.Message : error.Message;
			}
			this.endProgress();
			this.showError(message);
		}
	}

	showError (message) {
		const msgModalEl = document.querySelector('.add-to-cart-message-modal');

		if (msgModalEl) {
			const defaultHeading = 'Can\'t add product to shopping cart: ';
			if (message.includes(defaultHeading)) {
				message = message.replace(defaultHeading, '');
				message = `<h1>${defaultHeading}</h1><p>${message}</p>`;
			}
			const msgView = new AddToCartMsgView({
				el: msgModalEl,
				modal_selector: '.add-to-cart-message',
				navigation: null,
				modal_content: message
			});

			msgView.once_rendered = () => {
				msgView.init_modal();
				msgView.init_popup_events();
				msgView.open();
			};
		} else {
			window.alert(message);
		}
	}

	addManualOptions (options) {
		const url = Urls.Api.product_options_manual(this.cart_product.Id);

		fetch(url, {
			method: 'POST',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(options)
		})
		.then(fetch_status)
		.then(fetch_json)
		.then(response => {
			this.productAdded(response);
		})
		.catch(error => {
			fetch_error(error, true)
				.then(error_json => {
					this.processError(error_json);
				});
			});
	}

	productAdded (cart_data) {
		const data = Object.assign({}, cart_data, { source: 'Product', sku: this.cart_product.ProductSku, ProductId: this.cart_product.ProductId });
		const product_added_event = new CustomEvent('[add-to-cart]:product-added', { detail: data });

		document.dispatchEvent(product_added_event);

		if (this.show_popup) {
			this.showCartPopup()
				.then(() => {
					this.endProgress();
					if (!this.is_accessory) {
						this.set_state();
					}
				});
		} else {
			this.endProgress();
			if (!this.hasAddons) {
				this.goToCart();
			} else {
				this.set_state();
			}
		}
	}

	set_state() {
		if (this.allow_duplicates) {
			this.btnAddToCartElement.removeAttribute('disabled');
			this.btnAddToCartElement.classList.remove('progress');
			this.qtyElement.removeAttribute('disabled');
		} else {
			const btn_state = this.btnAddToCartElement.querySelector('span');

			this.btnAddToCartElement.classList.replace('progress', 'btn-success');
			btn_state.textContent = btn_state.getAttribute('data-state');
			this.qtyElement.classList.add('hide');
			this.in_cart = true;
		}
	}

	showCartPopup () {
		if (!this.cart_modal_view) {
			this.cart_modal_view = new AddToCartModalView( {
				el: this.cart_modal_placeholder,
				adv_source: this.adv_source,
				cart_product: this.cart_product
			} );
		}
		return new Promise(resolve => {
			this.cart_modal_view.once_rendered = () => {
				this.cart_modal_view.show_modal(this.cart_product);
				resolve(true);
			};
		});
	}

	getExternalData () {
		const product_data = {};
		const skipUserData = window.ProductSettings.SkipUserCartData || false;
		const userLocationCode = localStorage.getItem('userLocationCode');
		const userLocationName = localStorage.getItem('userLocationName');
		const userLocationAddress = localStorage.getItem('userLocationAddress');
		const userLocationDelivery = localStorage.getItem('userLocationDelivery');

		if (this.adv_source) {
			product_data.Source = 'ad';
		}
		if (window.price_match_price) {
			product_data.PriceMatchPrice = window.price_match_price;
		}
		if (window.price_match_url) {
			product_data.PriceMatchUrl = window.price_match_url;
		}
		if (this.variations) {
			product_data.DefaultOptions = true;
		} else {
			product_data.Options = this.product_data_options;
		}
		if (this.map_policy === 'ADD_TO_CART') {
			product_data.UseListPrice = true;
		}

		if (userLocationCode && !skipUserData) {
			product_data.Data = [
				{
					'Name': 'LocationCode',
					'Value': userLocationCode,
					'Type': 'hidden_string'
				}
			];
			if (userLocationName) {
				product_data.Data.push({
					'Name': 'LocationName',
					'Value': userLocationName,
					'Type': 'hidden_string'
				});
			}
			if (userLocationAddress) {
				product_data.Data.push({
					'Name': 'LocationAddress',
					'Value': userLocationAddress,
					'Type': 'hidden_string'
				});
			}
			if (userLocationDelivery) {
				product_data.Data.push({
					'Name': 'Delivery',
					'Value': userLocationDelivery,
					'Type': 'hidden_string'
				});
			}
		}

		return product_data;
	}

	goToCart () {
		window.location.href = Urls.Api.cart;
	}

	validateQty () {
		const num = this.qtyElement.value.replace(/[^\d]/,'');
		const num_min = parseInt(this.qtyElement.min);
		const num_max = parseInt(this.qtyElement.max);

		this.qtyElement.value = num;

		if (num < num_min) {
			this.qtyElement.value = num_min;
		}
		if (num > num_max) {
			this.qtyElement.value = num_max;
		}
		if (this.messageElement && !this.messageElement.classList.contains('d-none') && !this.qty_error) {
			this.messageElement.classList.add('d-none');
		}
	}

	validateQtyStep () {
		let qty_error = false;
		if (this.qtyElement) {
			const num = this.qtyElement.value.replace(/[^\d]/,'');
			let num_step = this.qtyElement.step;

			if (num_step) {
				num_step = parseInt(num_step);
				if (num_step > 1) {
					if (num % num_step !== 0) {
						this.showError('Please enter a quantity as a multiple of ' + num_step);
						qty_error = true;
					}
				}
			}
		}
		return qty_error;
	}

	handleEvents () {
		document.addEventListener('product-options:is_valid', event => {
			const detail = event.detail;

			if (detail) {
				this.valid_product_option = true;
			}
		});
		document.addEventListener('product-options:manual', event => {
			this.options_manual = event.detail || [];
		});
		document.addEventListener('product-options:selected', event => {
			const detail = event.detail;

			if (detail) {
				this.product_data_options = detail;
			}
		});
	}

	calculateMinQty () {
		let min_qty = window.SiteSettings.SHOPPING_CART_MIN_QTY ? window.SiteSettings.SHOPPING_CART_MIN_QTY : 1;
		let qty_step = window.ProductSettings.QtyStep ? window.ProductSettings.QtyStep : null;
		let min_order = window.ProductSettings.MinOrder ? window.ProductSettings.MinOrder : null;

		if (qty_step) {
			min_qty = qty_step;
		}

		if (qty_step && min_order) {
			if (this.product_price) {
				if (qty_step * this.product_price < min_order) {
					min_qty = Math.ceil(Math.ceil(min_order / this.product_price) / qty_step) * qty_step;
				}
			}
		}
		return min_qty;
	}

	calculateRewards () {
		if (this.qtyElement) {
			const qty = parseInt(this.qtyElement.value);

			this.rewardPointsElement = document.querySelector(this.selectors.rewardPoints);
			if (this.rewardPointsElement) {
				const rewardPointsCountElement = this.rewardPointsElement.querySelector('.count');
				const rewards = Math.round(qty * this.product_price);

				if (rewardPointsCountElement) {
					rewardPointsCountElement.textContent = rewards;
				}
				if (this.rewardPointsElement.classList.contains('hide')) {
					this.rewardPointsElement.classList.remove('hide');
				}
			}
		}
	}

	getProductPrice () {
		let price_els = document.querySelectorAll('.js-product-price-label');

		if (price_els.length && price_els[0].dataset.price) {
			this.product_price = parseFloat(price_els[0].dataset.price);
		}
	}
}
